/* global.js — progressive-enhancement custom elements. Vanilla, no globals, no deps.
   Every interactive part works without JS where the platform allows; JS upgrades it. */

/* ---------------------------------------------------------------------------
 * Async stylesheets — promote any <link rel="preload" as="style"
 * data-async-style> that the inline onload handler didn't already convert
 * (e.g. if a strict CSP dropped it). Idempotent and free when it already ran.
 * ------------------------------------------------------------------------- */
document.querySelectorAll('link[data-async-style][rel="preload"]').forEach((link) => {
  link.rel = 'stylesheet';
});

/* ---------------------------------------------------------------------------
 * Small helpers
 * ------------------------------------------------------------------------- */
const onReady = (fn) =>
  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', fn, { once: true })
    : fn();

async function fetchJSON(url, options) {
  const res = await fetch(url, options);
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  return res.json();
}

/**
 * Format an amount in cents into a Shopify money string.
 * Uses plain ASCII digits (never toLocaleString) so re-rendered prices match
 * the server-rendered `| money` output on RTL/Arabic browsers, which would
 * otherwise emit Arabic-Indic digits (٠١٢) and mismatch.
 * Handles all four Shopify money tokens.
 */
function formatMoney(cents, format) {
  const moneyFormat = format || '${{amount}}';
  const withCommas = (str) => str.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
  const amount = withCommas((cents / 100).toFixed(2));
  const noDecimals = withCommas(String(Math.round(cents / 100)));
  const amountWithComma = withCommas((cents / 100).toFixed(2))
    .replace(/,/g, ' ')
    .replace('.', ',')
    .replace(/ /g, '.');
  const noDecimalsWithComma = withCommas(String(Math.round(cents / 100))).replace(/,/g, '.');
  return moneyFormat
    .replace(/\{\{\s*amount\s*\}\}/, amount)
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amountWithComma)
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/, noDecimals)
    .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, noDecimalsWithComma);
}

/** Publish cart state so any listening component can refresh its count/preview. */
function publishCart(cart) {
  document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
}

function updateCartCounts(count) {
  document.querySelectorAll('[data-cart-count]').forEach((el) => {
    el.textContent = count;
    el.hidden = count === 0 && el.hasAttribute('data-hide-when-empty');
  });
}

document.addEventListener('cart:updated', (e) => {
  if (e.detail && typeof e.detail.item_count === 'number') updateCartCounts(e.detail.item_count);
});

/* ---------------------------------------------------------------------------
 * <header-nav> — mobile menu toggle (works without JS on desktop).
 * ------------------------------------------------------------------------- */
class HeaderNav extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-header-toggle]');
    this.nav = this.querySelector('[data-header-nav]');
    if (!this.toggle || !this.nav) return;
    this.backdrop = this.querySelector('[data-header-backdrop]');
    this.onToggle = this.onToggle.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.toggle.addEventListener('click', this.onToggle);
    this.addEventListener('keydown', this.onKeydown);

    // Drawer controls. Delegated so it keeps working for every submenu without
    // wiring a listener per row.
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-header-close]')) {
        this.setOpen(false);
        this.toggle.focus();
        return;
      }
      // Back out of a drill-down: close the <details> the button sits in.
      const back = event.target.closest('[data-menu-back]');
      if (back) {
        const panel = back.closest('details');
        if (panel) {
          panel.removeAttribute('open');
          const summary = panel.querySelector('summary');
          if (summary) summary.focus();
        }
      }
    });

    if (this.backdrop) {
      this.backdrop.addEventListener('click', () => {
        this.setOpen(false);
        this.toggle.focus();
      });
    }
  }
  disconnectedCallback() {
    if (this.toggle) this.toggle.removeEventListener('click', this.onToggle);
    this.removeEventListener('keydown', this.onKeydown);
    document.documentElement.style.removeProperty('overflow');
  }
  onToggle() {
    this.setOpen(this.toggle.getAttribute('aria-expanded') !== 'true');
  }
  setOpen(open) {
    this.toggle.setAttribute('aria-expanded', String(open));
    this.nav.setAttribute('data-open', String(open));
    if (this.backdrop) {
      this.backdrop.toggleAttribute('hidden', !open);
      this.backdrop.setAttribute('data-open', String(open));
    }
    // The drawer is a fixed overlay, so the page behind it must not scroll.
    // Asking the drawer whether it is actually an overlay beats hard-coding the
    // breakpoint here, which is a theme setting and can move.
    if (open && getComputedStyle(this.nav).position === 'fixed') {
      document.documentElement.style.setProperty('overflow', 'hidden');
    } else {
      document.documentElement.style.removeProperty('overflow');
    }
    if (open) {
      const first = this.nav.querySelector('a, button');
      if (first) first.focus();
    } else {
      // Collapse any open drill-down so the menu reopens at the root.
      this.nav.querySelectorAll('details[open]').forEach((d) => d.removeAttribute('open'));
    }
  }
  onKeydown(event) {
    if (event.key === 'Escape' && this.toggle.getAttribute('aria-expanded') === 'true') {
      this.setOpen(false);
      this.toggle.focus();
    }
  }
}

/* ---------------------------------------------------------------------------
 * Focus-trap utility for overlay components (drawer / modal).
 * ------------------------------------------------------------------------- */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function trapFocus(container, event) {
  const nodes = Array.from(container.querySelectorAll(FOCUSABLE)).filter(
    (n) => n.offsetParent !== null
  );
  if (!nodes.length) return;
  const first = nodes[0];
  const last = nodes[nodes.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

/* ---------------------------------------------------------------------------
 * <cart-drawer> — slide-out cart. Refreshes itself via the Section Rendering API.
 * ------------------------------------------------------------------------- */
class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('[data-cart-panel]');
    this.onKeydown = this.onKeydown.bind(this);
    this.close = this.close.bind(this);

    // Delegate all clicks on the persistent <cart-drawer> element so controls
    // keep working after the contents are replaced by the Section Rendering API.
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-close]') || e.target.closest('[data-cart-overlay]')) {
        e.preventDefault();
        this.close();
        return;
      }
      const remove = e.target.closest('[data-cart-remove]');
      if (remove) {
        e.preventDefault();
        this.changeLine(remove.dataset.line, 0);
        return;
      }
      const clear = e.target.closest('[data-cart-clear]');
      if (clear) {
        e.preventDefault();
        this.clearCart();
      }
    });

    // Live quantity changes inside the drawer (delegated — survives re-render).
    this.addEventListener('change', (e) => {
      const input = e.target.closest('[data-cart-quantity]');
      if (input) this.changeLine(input.dataset.line, input.value);
    });
  }

  open() {
    this.setAttribute('open', '');
    document.documentElement.style.setProperty('overflow', 'hidden');
    this.previouslyFocused = document.activeElement;
    document.addEventListener('keydown', this.onKeydown);
    requestAnimationFrame(() => {
      const focusTarget = this.querySelector('[data-cart-close]') || this.panel;
      if (focusTarget) focusTarget.focus();
    });
  }

  close() {
    this.removeAttribute('open');
    document.documentElement.style.removeProperty('overflow');
    document.removeEventListener('keydown', this.onKeydown);
    if (this.previouslyFocused && this.previouslyFocused.focus) this.previouslyFocused.focus();
  }

  onKeydown(event) {
    if (event.key === 'Escape') this.close();
    if (event.key === 'Tab') trapFocus(this, event);
  }

  async changeLine(line, quantity) {
    try {
      const cart = await fetchJSON(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity: Number(quantity), sections: this.sectionId }),
      });
      this.renderFromSections(cart.sections);
      publishCart(cart);
    } catch (err) {
      /* keep server state; reload as a safe fallback */
      window.location.reload();
    }
  }

  async clearCart() {
    try {
      const cart = await fetchJSON(`${window.Shopify?.routes?.root || '/'}cart/clear.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ sections: this.sectionId }),
      });
      this.renderFromSections(cart.sections);
      publishCart(cart);
    } catch (err) {
      window.location.reload();
    }
  }

  get sectionId() {
    return this.getAttribute('data-section-id') || 'cart-drawer';
  }

  /** Replace the drawer inner HTML from a Section Rendering API payload. */
  renderFromSections(sections) {
    if (!sections || !sections[this.sectionId]) return;
    const parsed = new DOMParser().parseFromString(sections[this.sectionId], 'text/html');
    const fresh = parsed.querySelector('[data-cart-contents]');
    const current = this.querySelector('[data-cart-contents]');
    if (fresh && current) current.replaceWith(fresh);
  }
}

/* ---------------------------------------------------------------------------
 * <product-form> — AJAX add-to-cart that opens the drawer. Falls back to a
 * normal form POST when JS is unavailable or the drawer is absent.
 * ------------------------------------------------------------------------- */
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.submitButton = this.form.querySelector('[type="submit"]');
    this.form.addEventListener('submit', this.onSubmit.bind(this));
  }

  async onSubmit(event) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return; // no drawer -> normal navigation to /cart
    event.preventDefault();
    if (this.submitButton) {
      this.submitButton.setAttribute('aria-busy', 'true');
      this.submitButton.disabled = true;
    }
    try {
      const formData = new FormData(this.form);
      // Safety net: some browsers (older iOS Safari) omit form-associated inputs
      // that live outside the <form> (the variant picker's id input), so make
      // sure a variant id is present before posting.
      if (!formData.get('id')) {
        const idInput =
          document.querySelector(`input[name="id"][form="${this.form.id}"]`) ||
          this.form.querySelector('[name="id"]') ||
          document.querySelector('[data-variant-id]');
        if (idInput && idInput.value) formData.set('id', idInput.value);
      }
      formData.append('sections', drawer.sectionId);
      const cart = await fetchJSON(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      drawer.renderFromSections(cart.sections);
      const fresh = await fetchJSON(`${window.Shopify?.routes?.root || '/'}cart.js`);
      publishCart(fresh);
      drawer.open();
    } catch (err) {
      this.form.submit(); // fall back to a full add
    } finally {
      if (this.submitButton) {
        this.submitButton.removeAttribute('aria-busy');
        this.submitButton.disabled = false;
      }
    }
  }
}

/* ---------------------------------------------------------------------------
 * <variant-selects> — switch variant on option change; update price, id,
 * availability, and the URL. Reads a <script type="application/json"> of variants.
 * ------------------------------------------------------------------------- */
class VariantSelects extends HTMLElement {
  connectedCallback() {
    this.variants = this.readVariants();
    this.idInput = this.querySelector('[data-variant-id]');
    this.addEventListener('change', this.onChange.bind(this));
  }
  readVariants() {
    const data = this.querySelector('[data-variants-json]');
    try {
      return data ? JSON.parse(data.textContent) : [];
    } catch (_) {
      return [];
    }
  }
  selectedOptions() {
    return Array.from(this.querySelectorAll('[data-option-selector]')).map((el) => {
      if (el.type === 'radio' || el.type === 'checkbox') {
        const checked = this.querySelector(`input[name="${el.name}"]:checked`);
        return checked ? checked.value : null;
      }
      return el.value;
    });
  }
  onChange() {
    const chosen = this.selectedOptions();
    const match = this.variants.find(
      (v) => JSON.stringify(v.options) === JSON.stringify(chosen.filter((c) => c !== null))
    );
    document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant: match, id: this.id } }));
    if (!match) {
      this.updateAddButton(true, this.dataset.unavailableText);
      return;
    }
    if (this.idInput) this.idInput.value = match.id;
    this.updatePrice(match);
    this.updateAddButton(!match.available, this.dataset.soldOutText);
    if (this.dataset.updateUrl !== 'false' && match.id) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', match.id);
      window.history.replaceState({}, '', url);
    }
  }
  updatePrice(variant) {
    const priceEl = document.getElementById(`price-${this.id}`);
    if (!priceEl) return;
    const money = (cents) => this.formatMoney(cents);
    let html = `<span class="price">${money(variant.price)}</span>`;
    if (variant.compare_at_price > variant.price) {
      const pct = Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price);
      html = `<span class="price price--sale">${money(variant.price)}</span> <s class="price__compare">${money(
        variant.compare_at_price
      )}</s> <span class="badge badge--sale">-${pct}%</span>`;
    }
    priceEl.innerHTML = html;
  }
  formatMoney(cents) {
    return formatMoney(cents, this.dataset.moneyFormat);
  }
  updateAddButton(disabled, text) {
    const button = document.getElementById(`add-${this.id}`);
    if (!button) return;
    button.toggleAttribute('disabled', disabled);
    if (text) {
      const label = button.querySelector('[data-add-label]') || button;
      label.textContent = text;
    }
  }
}

/* ---------------------------------------------------------------------------
 * <quantity-input> — accessible +/- steppers around a number input.
 * ------------------------------------------------------------------------- */
class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="number"]');
    if (!this.input) return;
    this.querySelectorAll('[data-qty-button]').forEach((btn) =>
      btn.addEventListener('click', () => this.step(btn.dataset.qtyButton === 'increase' ? 1 : -1))
    );
  }
  step(delta) {
    const min = Number(this.input.min || 0);
    const next = Math.max(min, (Number(this.input.value) || 0) + delta);
    this.input.value = next;
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

/* ---------------------------------------------------------------------------
 * <product-gallery> — swipeable image slider. The stage is a horizontal
 * scroll-snap track (native swipe on touch); thumbnails and desktop arrows
 * scroll it to a given slide, and the active thumbnail follows the current
 * slide. All positioning uses bounding-rect deltas so it works in LTR and RTL.
 * Without JS the stage is still a swipeable scroller.
 * ------------------------------------------------------------------------- */
class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.stage = this.querySelector('[data-gallery-stage]');
    this.slides = Array.from(this.querySelectorAll('[data-gallery-media]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    this.thumbsWrap = this.querySelector('[data-gallery-thumbs]');
    this.prevBtn = this.querySelector('[data-gallery-prev]');
    this.nextBtn = this.querySelector('[data-gallery-next]');
    if (!this.stage || !this.slides.length) return;
    this.setAttribute('data-enhanced', 'true');
    this.currentIndex = 0;

    this.thumbs.forEach((thumb) =>
      thumb.addEventListener('click', () => this.selectId(thumb.dataset.galleryThumb))
    );
    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.step(-1));
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.step(1));

    this.stage.addEventListener(
      'scroll',
      () => {
        window.cancelAnimationFrame(this._raf);
        this._raf = window.requestAnimationFrame(() => this.onScroll());
      },
      { passive: true }
    );

    document.addEventListener('variant:change', (e) => {
      const mediaId = e.detail?.variant?.featured_media?.id;
      if (mediaId) this.selectId(String(mediaId));
    });

    this.syncActive(0);
  }

  indexOfId(id) {
    return this.slides.findIndex((s) => s.dataset.galleryMedia === id);
  }

  selectId(id) {
    const i = this.indexOfId(id);
    if (i >= 0) this.scrollToIndex(i);
  }

  step(dir) {
    this.scrollToIndex(Math.min(this.slides.length - 1, Math.max(0, this.currentIndex + dir)));
  }

  scrollToIndex(i) {
    const slide = this.slides[i];
    if (!slide) return;
    // Delta from the slide's left edge to the stage's left edge — works LTR & RTL.
    const delta = slide.getBoundingClientRect().left - this.stage.getBoundingClientRect().left;
    this.stage.scrollBy({ left: delta, behavior: 'smooth' });
    this.syncActive(i);
  }

  onScroll() {
    const stageRect = this.stage.getBoundingClientRect();
    const center = stageRect.left + stageRect.width / 2;
    let best = 0;
    let bestDist = Infinity;
    this.slides.forEach((s, i) => {
      const r = s.getBoundingClientRect();
      const dist = Math.abs(r.left + r.width / 2 - center);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    });
    this.syncActive(best);
  }

  syncActive(i) {
    // Scroll fires many times per gesture; bail when nothing changed so the
    // attribute writes below don't invalidate layout for the next rect read.
    if (this.currentIndex === i && this._synced) return;
    this._synced = true;
    this.currentIndex = i;
    this.thumbs.forEach((t, idx) => {
      const value = String(idx === i);
      if (t.getAttribute('aria-current') !== value) t.setAttribute('aria-current', value);
    });
    // Keep the active thumbnail visible in the thumb scroller.
    const activeThumb = this.thumbs[i];
    if (activeThumb && this.thumbsWrap) {
      const twRect = this.thumbsWrap.getBoundingClientRect();
      const aRect = activeThumb.getBoundingClientRect();
      const delta = aRect.left + aRect.width / 2 - (twRect.left + twRect.width / 2);
      this.thumbsWrap.scrollBy({ left: delta, behavior: 'smooth' });
    }
    const atStart = i <= 0;
    const atEnd = i >= this.slides.length - 1;
    if (this.prevBtn && this.prevBtn.disabled !== atStart) this.prevBtn.disabled = atStart;
    if (this.nextBtn && this.nextBtn.disabled !== atEnd) this.nextBtn.disabled = atEnd;
  }
}

/* ---------------------------------------------------------------------------
 * Cart-drawer openers: header cart button + any [data-cart-toggle].
 * ------------------------------------------------------------------------- */
onReady(() => {
  document.querySelectorAll('[data-cart-toggle]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return; // let the link navigate to /cart
      e.preventDefault();
      drawer.open();
    });
  });
});

/* --- Quantity inputs on the cart PAGE: auto-submit closest form on change --- */
document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLElement) || !input.matches('[data-quantity-input]')) return;
  const form = input.closest('form');
  if (form && typeof form.requestSubmit === 'function') form.requestSubmit();
});

/* --- Reveal on scroll ----------------------------------------------------- */
(function initReveal() {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const elements = document.querySelectorAll('.reveal');
  if (!elements.length) return;
  if (reduceMotion || !('IntersectionObserver' in window)) {
    elements.forEach((el) => el.classList.add('is-visible'));
    return;
  }
  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          obs.unobserve(entry.target);
        }
      });
    },
    { rootMargin: '0px 0px -10% 0px' }
  );
  elements.forEach((el) => observer.observe(el));
})();

/* --- Close header/localization <details> menus on outside-click / Escape --- */
(function initMenuDismiss() {
  const SELECTOR = 'details.header__dropdown[open], details.localization[open]';
  // While the mobile drawer is open a header dropdown IS the current drill-down
  // panel, so an outside click inside the drawer must not collapse it — the
  // back button and the backdrop handle that.
  const drawerOpen = () => !!document.querySelector('[data-header-nav][data-open="true"]');
  document.addEventListener('click', (event) => {
    document.querySelectorAll(SELECTOR).forEach((menu) => {
      if (menu.classList.contains('header__dropdown') && drawerOpen()) return;
      if (!menu.contains(event.target)) menu.removeAttribute('open');
    });
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll(SELECTOR).forEach((menu) => {
      menu.removeAttribute('open');
      const summary = menu.querySelector('summary');
      if (summary) summary.focus();
    });
  });
})();

/* --- Header dropdowns: open on hover (desktop), click on touch, keyboard-safe -- */
(function initHeaderHover() {
  const dropdowns = document.querySelectorAll('.header__dropdown');
  if (!dropdowns.length) return;
  // Both the breakpoint and hover-to-open are Header settings, published on the
  // element so the CSS media queries and this stay in step.
  const header = document.querySelector('[data-header]');
  const breakpoint = (header && parseInt(header.dataset.navBreakpoint, 10)) || 990;
  const hoverEnabled = !header || header.dataset.hoverOpen !== 'false';
  const desktop = window.matchMedia('(hover: hover) and (min-width: ' + breakpoint + 'px)');
  const closeOthers = (keep) =>
    document.querySelectorAll('.header__dropdown[open]').forEach((d) => {
      if (d !== keep) d.removeAttribute('open');
    });

  dropdowns.forEach((dropdown) => {
    const host = dropdown.closest('.header__menu-item') || dropdown;
    const summary = dropdown.querySelector('summary');
    let timer;

    host.addEventListener('mouseenter', () => {
      if (!desktop.matches || !hoverEnabled) return;
      clearTimeout(timer);
      closeOthers(dropdown);
      dropdown.setAttribute('open', '');
    });
    host.addEventListener('mouseleave', () => {
      if (!desktop.matches || !hoverEnabled) return;
      clearTimeout(timer);
      timer = setTimeout(() => dropdown.removeAttribute('open'), 160);
    });

    // On desktop, hover drives it — stop the click from toggling. Touch keeps native click.
    if (summary) {
      summary.addEventListener('click', (event) => {
        if (desktop.matches && hoverEnabled) event.preventDefault();
      });
    }

    // Keyboard: open when focus enters, close when it leaves the dropdown.
    dropdown.addEventListener('focusin', () => {
      if (!desktop.matches) return;
      clearTimeout(timer);
      closeOthers(dropdown);
      dropdown.setAttribute('open', '');
    });
    dropdown.addEventListener('focusout', (event) => {
      if (!desktop.matches) return;
      if (!dropdown.contains(event.relatedTarget)) dropdown.removeAttribute('open');
    });
  });
})();

/* ---------------------------------------------------------------------------
 * setupCarousel — turns a [data-rec-scroller] row into an arrow-driven slider.
 * Arrows show on desktop when the row overflows; mobile uses peek + scroll-snap.
 * ------------------------------------------------------------------------- */
function setupCarousel(host) {
  const scroller = host.querySelector('[data-rec-scroller]');
  const prev = host.querySelector('[data-rec-prev]');
  const next = host.querySelector('[data-rec-next]');
  if (!scroller) return;
  const carousel = scroller.parentElement || host;
  // Read the document `dir` attribute rather than computed style: an attribute
  // read is free, while getComputedStyle flushes pending style/layout work.
  const rtl = () => document.documentElement.dir === 'rtl';
  const page = () => {
    const item = scroller.firstElementChild;
    const gap = parseFloat(getComputedStyle(scroller).columnGap) || 0;
    const step = item ? item.getBoundingClientRect().width + gap : scroller.clientWidth;
    return Math.max(step, scroller.clientWidth * 0.5);
  };
  const update = () => {
    const max = scroller.scrollWidth - scroller.clientWidth - 1;
    const x = Math.abs(scroller.scrollLeft);
    const scrollable = scroller.scrollWidth > scroller.clientWidth + 4;
    const atStart = x <= 1;
    const atEnd = x >= max;
    // Write only on change: a redundant assignment still dirties layout, so the
    // next scroll event's scrollWidth read would force a synchronous reflow.
    if (carousel.hasAttribute('data-scrollable') !== scrollable) {
      carousel.toggleAttribute('data-scrollable', scrollable);
    }
    if (prev && prev.disabled !== atStart) prev.disabled = atStart;
    if (next && next.disabled !== atEnd) next.disabled = atEnd;
  };
  // Coalesce scroll/resize bursts into one measurement per frame.
  let rafId = 0;
  const queueUpdate = () => {
    if (rafId) return;
    rafId = requestAnimationFrame(() => {
      rafId = 0;
      update();
    });
  };
  if (prev) prev.addEventListener('click', () => scroller.scrollBy({ left: -page() * (rtl() ? -1 : 1), behavior: 'smooth' }));
  if (next) next.addEventListener('click', () => scroller.scrollBy({ left: page() * (rtl() ? -1 : 1), behavior: 'smooth' }));
  scroller.addEventListener('scroll', queueUpdate, { passive: true });
  window.addEventListener('resize', queueUpdate, { passive: true });
  update();
}

/* ---------------------------------------------------------------------------
 * <product-recommendations> — fetches Shopify's Product Recommendations API
 * section and swaps in the result (recommendations is empty on first render).
 * ------------------------------------------------------------------------- */
class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    const url = this.dataset.url;
    if (!url) return;
    fetch(url)
      .then((res) => (res.ok ? res.text() : Promise.reject(res.status)))
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const fresh = doc.querySelector('product-recommendations');
        if (fresh && fresh.innerHTML.trim().length) {
          this.innerHTML = fresh.innerHTML;
          setupCarousel(this);
        }
      })
      .catch(() => {});
  }
}

/* ---------------------------------------------------------------------------
 * <recently-viewed> — renders products from localStorage (no network).
 * ------------------------------------------------------------------------- */
class RecentlyViewed extends HTMLElement {
  connectedCallback() {
    let list = [];
    try { list = JSON.parse(localStorage.getItem('lamya:recently-viewed')) || []; } catch (e) { list = []; }
    const path = window.location.pathname;
    list = list.filter((p) => p && p.handle && !path.endsWith('/products/' + p.handle));
    const limit = parseInt(this.dataset.limit, 10) || 6;
    list = list.slice(0, limit);
    if (!list.length) return;
    const grid = this.querySelector('[data-recently-viewed-grid]');
    const tpl = this.querySelector('[data-recently-viewed-card]');
    if (!grid || !tpl) return;
    // Allow absolute http(s) URLs and root-relative /paths (e.g. /products/handle); block javascript: and //external.
    const safeUrl = (u) => {
      if (typeof u !== 'string') return null;
      if (/^https?:\/\//.test(u)) return u;
      if (/^\/(?!\/)/.test(u)) return u;
      return null;
    };
    list.forEach((p) => {
      const node = tpl.content.firstElementChild.cloneNode(true);
      const link = node.querySelector('a');
      link.href = safeUrl(p.url) || '#';
      const img = node.querySelector('img');
      const src = safeUrl(p.image);
      if (src) { img.src = src; img.alt = p.title || ''; } else { img.remove(); }
      const vendor = node.querySelector('.rv-card__vendor');
      if (p.vendor) { vendor.textContent = p.vendor; } else { vendor.remove(); }
      node.querySelector('.rv-card__title').textContent = p.title || '';
      const price = node.querySelector('.rv-card__price');
      price.textContent = p.price || '';
      if (p.compare_at) {
        const was = document.createElement('s');
        was.className = 'rv-card__was';
        was.textContent = p.compare_at;
        price.append(' ', was);
      }
      grid.appendChild(node);
    });
    this.hidden = false;
    setupCarousel(this);
  }
}

/* ---------------------------------------------------------------------------
 * <bought-together> — "Frequently bought together": live total + one request
 * that adds every checked item to the cart, then opens the drawer.
 * ------------------------------------------------------------------------- */
class BoughtTogether extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('[data-fbt-item]'));
    this.totalEl = this.querySelector('[data-fbt-total]');
    this.countEl = this.querySelector('[data-fbt-count]');
    this.addBtn = this.querySelector('[data-fbt-add]');
    if (!this.items.length || !this.addBtn) return;
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.items.forEach((input) => input.addEventListener('change', () => this.update()));
    this.addBtn.addEventListener('click', () => this.add());
    this.update();
  }
  checked() { return this.items.filter((input) => input.checked); }
  formatMoney(cents) {
    return formatMoney(cents, this.moneyFormat);
  }
  update() {
    const checked = this.checked();
    const total = checked.reduce((sum, input) => sum + (parseInt(input.dataset.price, 10) || 0), 0);
    if (this.totalEl) this.totalEl.textContent = this.formatMoney(total);
    if (this.countEl) this.countEl.textContent = String(checked.length);
    this.addBtn.disabled = checked.length === 0;
  }
  async add() {
    const ids = this.checked().map((input) => ({ id: Number(input.value), quantity: 1 }));
    if (!ids.length) return;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const drawer = document.querySelector('cart-drawer');
    this.addBtn.setAttribute('aria-busy', 'true');
    this.addBtn.disabled = true;
    try {
      const body = { items: ids };
      if (drawer) body.sections = drawer.sectionId;
      const cart = await fetchJSON(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      if (drawer && cart.sections) drawer.renderFromSections(cart.sections);
      const fresh = await fetchJSON(`${root}cart.js`);
      publishCart(fresh);
      if (drawer) drawer.open();
    } catch (err) {
      window.location.href = `${root}cart`;
    } finally {
      this.addBtn.removeAttribute('aria-busy');
      this.update();
    }
  }
}

/* ---------------------------------------------------------------------------
 * <share-actions> — WhatsApp link (native), plus Copy-link and Instagram
 * buttons that copy the product URL to the clipboard (Instagram has no web
 * share URL, so we copy the link and open Instagram to paste). Shows brief
 * inline feedback.
 * ------------------------------------------------------------------------- */
class ShareActions extends HTMLElement {
  connectedCallback() {
    this.url = this.dataset.shareUrl || window.location.href;
    this.feedback = this.querySelector('[data-share-feedback]');
    this.addEventListener('click', (e) => {
      const copy = e.target.closest('[data-share-copy]');
      const ig = e.target.closest('[data-share-instagram]');
      if (copy) {
        e.preventDefault();
        this.copy(this.dataset.copiedText || 'Link copied');
      } else if (ig) {
        e.preventDefault();
        this.copy(this.dataset.instagramText || 'Link copied');
        window.open('https://www.instagram.com/', '_blank', 'noopener');
      }
    });
  }
  async copy(message) {
    try {
      await navigator.clipboard.writeText(this.url);
    } catch (_) {
      const ta = document.createElement('textarea');
      ta.value = this.url;
      ta.setAttribute('readonly', '');
      ta.style.position = 'absolute';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (e) { /* no-op */ }
      ta.remove();
    }
    this.showFeedback(message);
  }
  showFeedback(message) {
    if (!this.feedback) return;
    this.feedback.textContent = message;
    this.feedback.hidden = false;
    clearTimeout(this._t);
    this._t = setTimeout(() => { this.feedback.hidden = true; }, 3000);
  }
}

/* ---------------------------------------------------------------------------
 * <delivery-cutoff> — live "order within X to get next-day delivery" countdown.
 * Config comes from data-attributes (set from Theme settings via the snippet).
 * Time is computed against the store's UTC offset so every shopper — whatever
 * their local clock — sees the same, correct countdown to the daily cutoff.
 * ------------------------------------------------------------------------- */
class DeliveryCutoff extends HTMLElement {
  connectedCallback() {
    this.hour = parseInt(this.dataset.hour, 10) || 0;
    this.minute = parseInt(this.dataset.minute, 10) || 0;
    this.offset = parseFloat(this.dataset.offset || '0') || 0;

    const before = this.dataset.before || '{time}';
    const idx = before.indexOf('{time}');
    // NB: `prefix` is a read-only DOM property on Element — use a non-colliding name.
    this.msgPrefix = idx >= 0 ? before.slice(0, idx) : before + ' ';
    this.msgSuffix = idx >= 0 ? before.slice(idx + '{time}'.length) : '';
    this.after = this.dataset.after || '';
    this.hrs = this.dataset.hrs || 'Hrs';
    this.mins = this.dataset.mins || 'Mins';
    this.secs = this.dataset.secs || 'Secs';

    this.prefixEl = this.querySelector('[data-cutoff-prefix]');
    this.countEl = this.querySelector('[data-cutoff-count]');
    this.suffixEl = this.querySelector('[data-cutoff-suffix]');
    if (!this.countEl) return;

    this.tick();
    this._t = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this._t);
  }

  // Seconds remaining until today's cutoff, in store-local time. Negative = passed.
  remainingSeconds() {
    const now = new Date();
    const store = new Date(now.getTime() + this.offset * 3600000);
    const secondOfDay =
      store.getUTCHours() * 3600 + store.getUTCMinutes() * 60 + store.getUTCSeconds();
    return this.hour * 3600 + this.minute * 60 - secondOfDay;
  }

  format(total) {
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    if (h > 0) return `${h} ${this.hrs} ${m} ${this.mins}`;
    if (m > 0) return `${m} ${this.mins} ${s} ${this.secs}`;
    return `${s} ${this.secs}`;
  }

  tick() {
    const remaining = this.remainingSeconds();
    if (remaining > 0) {
      if (this.prefixEl) this.prefixEl.textContent = this.msgPrefix;
      if (this.suffixEl) this.suffixEl.textContent = this.msgSuffix;
      this.countEl.textContent = this.format(remaining);
      this.countEl.hidden = false;
    } else {
      if (this.prefixEl) this.prefixEl.textContent = this.after;
      if (this.suffixEl) this.suffixEl.textContent = '';
      this.countEl.textContent = '';
      this.countEl.hidden = true;
    }
  }
}

/* --- Register elements ---------------------------------------------------- */
const define = (name, ctor) => {
  if (!customElements.get(name)) customElements.define(name, ctor);
};
define('header-nav', HeaderNav);
define('cart-drawer', CartDrawer);
define('product-form', ProductForm);
define('variant-selects', VariantSelects);
define('quantity-input', QuantityInput);
define('product-gallery', ProductGallery);
define('product-recommendations', ProductRecommendations);
define('recently-viewed', RecentlyViewed);
define('bought-together', BoughtTogether);
define('delivery-cutoff', DeliveryCutoff);
define('share-actions', ShareActions);
